import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Loader2, Save, RotateCcw, Eye, Lock, ChevronRight, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import { cn } from '@/lib/utils';

/**
 * Editor for the candidate-facing email copy.
 *
 * Companies control the wording; the structural parts of each email —
 * credentials, the details table, the call to action — are rendered by the
 * server and shown here as locked blocks. That split is the whole point: tone
 * is theirs, but an invitation can never go out missing its login details.
 */

const COPY_FIELDS = [
    { key: 'subject',   label: 'Subject',        rows: 0, hint: 'What the candidate sees in their inbox.' },
    { key: 'greeting',  label: 'Greeting',       rows: 0, hint: 'The opening line.' },
    { key: 'introBody', label: 'Opening message', rows: 4, hint: 'Appears before the details.' },
];

const CLOSING_FIELDS = [
    { key: 'outroBody', label: 'Closing message', rows: 4, hint: 'Appears after the details.' },
    { key: 'signature', label: 'Sign-off',        rows: 0, hint: 'Optional. Leave blank to omit.' },
];

/** Insert text at the cursor, so variable chips land where the user is typing. */
function insertAtCursor(el, value, token) {
    if (!el) return value + token;
    const start = el.selectionStart ?? value.length;
    const end   = el.selectionEnd   ?? value.length;
    return value.slice(0, start) + token + value.slice(end);
}

function VariableChips({ variables, onInsert }) {
    return (
        <div className="flex flex-wrap gap-1">
            {variables.map(v => (
                <button
                    key={v.key}
                    type="button"
                    onClick={() => onInsert(v.key)}
                    title={`${v.label} — e.g. ${v.example}`}
                    className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                    {v.key}
                </button>
            ))}
        </div>
    );
}

function LockedBlocks({ blocks }) {
    if (!blocks?.length) return null;
    return (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3.5">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Lock className="h-3 w-3" />
                Added automatically
            </div>
            <ul className="space-y-1">
                {blocks.map(b => (
                    <li key={b} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                        {b}
                    </li>
                ))}
            </ul>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/80">
                These are filled in for each candidate and cannot be edited — it keeps
                details like login credentials from going missing.
            </p>
        </div>
    );
}


/** Sample values mirrored from the server, so the preview reads as a real email. */
const SAMPLE = {
    candidateName: 'Priya',
    companyName:   'Your Company',
    jobTitle:      'Senior Backend Engineer',
    department:    'Engineering',
    scheduledAt:   'Monday, 12 May 2026, 10:30 AM',
    durationMinutes: '45',
    roundName:     'Round 2 — Technical',
};

/** Substitute {{tokens}} locally so the preview tracks typing without a round trip. */
function fill(text, vars) {
    if (!text) return '';
    return text.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '').replace(/[ \t]{2,}/g, ' ').trim();
}

/**
 * Renders the template as the candidate would receive it — inbox chrome, sender
 * line, and the locked blocks shown in position rather than as a list. Seeing
 * where the credentials land is what makes the hybrid model legible.
 */
function EmailPreview({ template, form, companyName }) {
    const vars = { ...SAMPLE, companyName: companyName || SAMPLE.companyName };
    const subject   = fill(form.subject,   vars);
    const greeting  = fill(form.greeting,  vars);
    const intro     = fill(form.introBody, vars);
    const outro     = fill(form.outroBody, vars);
    const signature = fill(form.signature, vars);

    return (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
            {/* Inbox header */}
            <div className="space-y-1.5 border-b border-border bg-muted/50 px-3.5 py-2.5">
                <p className="truncate text-xs font-semibold text-foreground">
                    {subject || <span className="italic text-muted-foreground">No subject</span>}
                </p>
                <p className="truncate text-[10px] text-muted-foreground">
                    {companyName || 'Your Company'} &lt;careers@yourcompany.com&gt; &middot; to Priya
                </p>
            </div>

            {/* Body */}
            <div className="space-y-3 p-4 text-xs">
                {greeting && <p className="font-semibold text-foreground">{greeting}</p>}
                {intro    && <p className="leading-relaxed text-muted-foreground">{intro}</p>}

                {template.systemBlocks?.length > 0 && (
                    <div className="space-y-1.5 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2.5">
                        <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            <Lock className="h-2.5 w-2.5" />Added automatically
                        </p>
                        {template.systemBlocks.map(b => (
                            <div key={b} className="h-5 rounded bg-muted/70 px-2 text-[10px] leading-5 text-muted-foreground">
                                {b}
                            </div>
                        ))}
                    </div>
                )}

                {outro     && <p className="leading-relaxed text-muted-foreground">{outro}</p>}
                {signature && <p className="font-medium text-foreground">{signature}</p>}
            </div>

            <div className="border-t border-border px-4 py-2 text-center text-[10px] text-muted-foreground">
                Sent by {companyName || 'Your Company'}
            </div>
        </div>
    );
}

function TemplateEditor({ template, onSaved, companyName }) {
    const [form,   setForm]   = useState({});
    const [saving, setSaving] = useState(false);
    const [dirty,  setDirty]  = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const refs = useRef({});

    useEffect(() => {
        setForm({
            subject:   template.subject   || '',
            greeting:  template.greeting  || '',
            introBody: template.introBody || '',
            outroBody: template.outroBody || '',
            signature: template.signature || '',
            isActive:  template.isActive !== false,
        });
        setDirty(false);
    }, [template]);

    const setField = (key) => (e) => {
        setForm(prev => ({ ...prev, [key]: e.target.value }));
        setDirty(true);
    };

    const insertToken = (key) => (token) => {
        setForm(prev => ({ ...prev, [key]: insertAtCursor(refs.current[key], prev[key] || '', token) }));
        setDirty(true);
        requestAnimationFrame(() => refs.current[key]?.focus());
    };

    const save = async () => {
        if (!form.subject?.trim()) { toast.error('Subject cannot be empty.'); return; }
        setSaving(true);
        try {
            const res = await apiClient.updateEmailTemplate(template.type, form);
            onSaved(res.data);
            setDirty(false);
            toast.success('Template saved.');
        } catch (err) {
            toast.error(err.message || 'Could not save the template.');
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        try {
            const res = await apiClient.resetEmailTemplate(template.type);
            onSaved(res.data);
            setResetOpen(false);
            toast.success('Reset to the default wording.');
        } catch (err) {
            toast.error(err.message || 'Could not reset the template.');
        }
    };

    const toggleActive = async (next) => {
        try {
            const res = await apiClient.updateEmailTemplate(template.type, { isActive: next });
            onSaved(res.data);
            setForm(prev => ({ ...prev, isActive: next }));
            toast.success(next ? 'This email will be sent.' : 'This email is switched off.');
        } catch (err) {
            toast.error(err.message || 'Could not update.');
        }
    };

    const field = ({ key, label, rows, hint }) => (
        <div key={key} className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor={`tpl-${key}`}>{label}</Label>
                <VariableChips variables={template.variables} onInsert={insertToken(key)} />
            </div>
            {rows ? (
                <textarea
                    id={`tpl-${key}`}
                    ref={el => { refs.current[key] = el; }}
                    rows={rows}
                    value={form[key] || ''}
                    onChange={setField(key)}
                    className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
            ) : (
                <Input
                    id={`tpl-${key}`}
                    ref={el => { refs.current[key] = el; }}
                    value={form[key] || ''}
                    onChange={setField(key)}
                    className="h-10"
                />
            )}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
        </div>
    );

    return (
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground">{template.name}</h3>
                    <p className="text-xs text-muted-foreground">{template.description}</p>
                </div>
                <div className="flex items-center gap-2">
                    {template.isDefault && (
                        <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            Default wording
                        </span>
                    )}
                </div>
            </div>

            {template.canDisable && (
                <label className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/20 p-3">
                    <input
                        type="checkbox"
                        checked={form.isActive !== false}
                        onChange={e => toggleActive(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-input"
                    />
                    <span className="text-xs">
                        <span className="font-medium text-foreground">Send this email</span>
                        <span className="block text-muted-foreground">
                            When off, rejected candidates are not notified at all.
                        </span>
                    </span>
                </label>
            )}

            {COPY_FIELDS.map(field)}
            <LockedBlocks blocks={template.systemBlocks} />
            {CLOSING_FIELDS.map(field)}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
                {!template.isDefault ? (
                    <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground"
                        onClick={() => setResetOpen(true)}>
                        <RotateCcw className="h-3.5 w-3.5" />Reset to default
                    </Button>
                ) : <span />}
                <Button size="sm" className="gap-1.5" onClick={save} disabled={saving || !dirty}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save
                </Button>
            </div>
          </div>

          {/* ── Live preview ────────────────────────────────────────────── */}
          <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[19rem]">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  <Eye className="h-3 w-3" />Live preview
              </p>
              <EmailPreview template={template} form={form} companyName={companyName} />
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  Updates as you type, using sample candidate details.
              </p>
          </aside>


            <ConfirmDialog
                open={resetOpen}
                onOpenChange={setResetOpen}
                title="Reset to default wording?"
                description="Your edits to this email will be replaced with EchoHire's standard copy. This cannot be undone."
                confirmLabel="Reset"
                variant="destructive"
                onConfirm={reset}
            />
        </div>
    );
}

export default function EmailTemplatesSection() {
    const [templates, setTemplates] = useState([]);
    const [activeType, setActiveType] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                // The company name only decorates the preview, so a failure there
                // must not stop the templates loading.
                const [tplRes, profileRes] = await Promise.allSettled([
                    apiClient.getEmailTemplates(),
                    apiClient.getCompanyProfile(),
                ]);
                if (tplRes.status === 'rejected') throw tplRes.reason;

                setTemplates(tplRes.value.data || []);
                setActiveType(tplRes.value.data?.[0]?.type || null);
                if (profileRes.status === 'fulfilled') {
                    setCompanyName(profileRes.value.data?.companyName || '');
                }
            } catch (err) {
                toast.error(err.message || 'Could not load email templates.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleSaved = useCallback((updated) => {
        setTemplates(prev => prev.map(t => t.type === updated.type ? updated : t));
    }, []);

    if (loading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/50" />
                ))}
            </div>
        );
    }

    const active = templates.find(t => t.type === activeType);

    return (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            {/* Template list */}
            <nav className="flex gap-1.5 overflow-x-auto pb-1 lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible lg:pb-0">
                {templates.map(t => {
                    const selected = t.type === activeType;
                    return (
                        <button
                            key={t.type}
                            type="button"
                            onClick={() => setActiveType(t.type)}
                            className={cn(
                                'flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors lg:w-full',
                                selected
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                        >
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                            {t.isActive === false
                                ? <span className="shrink-0 text-[10px] text-muted-foreground">off</span>
                                : !t.isDefault && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" title="Customised" />}
                            <ChevronRight className={cn('hidden h-3 w-3 shrink-0 lg:block', selected ? 'opacity-100' : 'opacity-0')} />
                        </button>
                    );
                })}
            </nav>

            <div className="min-w-0 flex-1">
                {active
                    ? <TemplateEditor template={active} onSaved={handleSaved} companyName={companyName} />
                    : <p className="text-sm text-muted-foreground">Select a template to edit.</p>}
            </div>
        </div>
    );
}
